/**
 * Vidu S1 Live personas — table talk + blackjack dealer.
 * API key stays on Worker; SPA only uses share URLs + /api/vidu/*.
 *
 * Two voice↔chat systems share the same Live stack:
 *  1. BetUDontBet — HU / AI table talk (David Emmons energy)
 *  2. Racalvin — blackjack dealer (Pirate King of the Ghost Fleet)
 */

export type ViduPersonaId = "budb" | "racalvin";

export interface ViduPersona {
  id: ViduPersonaId;
  name: string;
  handle: string;
  role: "table_buddy" | "blackjack_dealer";
  /** Share-avatar iframe face */
  shareId: string;
  shareUrl: string;
  /** Default Live image_uri fallback */
  imageUri: string;
  /** Preferred clone / preset voice name (override via env) */
  defaultVoice: string;
  /** Env var for custom cloned voice */
  voiceEnvKey: "VIDU_VOICE_NAME" | "VIDU_VOICE_RACALVIN";
  greeting: string;
  /** Short avatar.persona for Create Live */
  personaLines: string[];
  knowledgeIds: string[];
}

/** BetUDontBet — HU VS BUDB table talk (existing share avatar) */
export const PERSONA_BUDB: ViduPersona = {
  id: "budb",
  name: "BetUDontBet",
  handle: "BUDB",
  role: "table_buddy",
  shareId: "3415766548095076",
  shareUrl:
    "https://www.vidu.com/vidu-stream/share-avatar/3415766548095076",
  imageUri: "https://poker.grudge-studio.com/media/landing-hero.jpg",
  defaultVoice: "Tina",
  voiceEnvKey: "VIDU_VOICE_NAME",
  greeting:
    "Yo — BetUDontBet. Chill pro kid. Ask me GBUX or how to sit a table.",
  personaLines: [
    "You are BetUDontBet (BUDB) on poker.grudge-studio.com.",
    "Kid mascot + chill pro poker energy (David Emmons vibe): funny, calm, never preachy.",
    "1–2 short spoken sentences. Light slang ok. No slurs.",
    "GBUX: play chips = server ledger. Deposit on-chain GBUX → BUDBai → play 1:1.",
    "Cash-out only via email games@grudgestudio.org (staff). Never invent balances.",
    "Join: Lobby → HU VS BUDB or AI Lab → Join with play GBUX.",
    "First person as BetUDontBet. Table buddy, not a dealer.",
  ],
  knowledgeIds: ["gbux_play", "join_table", "phantom", "blackjack_hint"],
};

/**
 * Racalvin — blackjack dealer
 * Share avatar: https://www.vidu.com/vidu-stream/share-avatar/3417104223780077
 */
export const PERSONA_RACALVIN: ViduPersona = {
  id: "racalvin",
  name: "Racalvin",
  handle: "Racalvin",
  role: "blackjack_dealer",
  shareId: "3417104223780077",
  shareUrl:
    "https://www.vidu.com/vidu-stream/share-avatar/3417104223780077",
  imageUri:
    "https://poker.grudge-studio.com/slots/bg/racalvin-banner.webp",
  defaultVoice: "Tina",
  voiceEnvKey: "VIDU_VOICE_RACALVIN",
  greeting:
    "Racalvin dealing. Place your bets, sea-dog — three hands, fair shoe, play GBUX.",
  personaLines: [
    "You are Racalvin — Pirate King of the Ghost Fleet — blackjack dealer at poker.grudge-studio.com/blackjack.",
    "Theatrical pirate calm: witty, fair, never cheat or invent card outcomes (server shoe is authority).",
    "1–2 short spoken sentences. Pirate flair ok, no slurs.",
    "Rules: 3-deck CSPRNG shoe, up to 3 hands, 3:2 blackjack, play GBUX ledger.",
    "Never invent balances, never reveal upcoming shoe cards, never claim you move real money.",
    "First person as Racalvin the dealer.",
  ],
  knowledgeIds: ["blackjack_rules", "gbux_play", "phantom"],
};

export const VIDU_PERSONAS: Record<ViduPersonaId, ViduPersona> = {
  budb: PERSONA_BUDB,
  racalvin: PERSONA_RACALVIN,
};

export function resolveViduPersona(
  id?: string | null,
): ViduPersona {
  const k = String(id || "budb").toLowerCase();
  if (k === "racalvin" || k === "dealer" || k === "bj") {
    return PERSONA_RACALVIN;
  }
  return PERSONA_BUDB;
}

export function personaSystemPrompt(p: ViduPersona): string {
  return p.personaLines.join("\n");
}
